<script setup lang="ts">
import { computed } from 'vue';

// Toggle chip for the sidebar's insight filters. A native button carrying `aria-pressed`
// rather than a styled div, so the whole filter row is reachable and operable by keyboard.
//
// `category` mirrors the ChipCategory vocabulary the card's insight chips already use
// (`candidate-insights-chips.ts`), so a filter chip and the card chip it corresponds to
// land in the same color family and the sidebar reads as part of one system.
type FilterChipCategory = 'preference' | 'intent' | 'language';

const props = withDefaults(
  defineProps<{
    label: string;
    selected: boolean;
    category: FilterChipCategory;
    /**
     * Facet count shown after the label. Optional: with no count the chip renders
     * label-only, which is a complete control on its own.
     */
    count?: number;
  }>(),
  { count: undefined },
);

defineEmits<{ toggle: [] }>();

// Full literal class strings so Tailwind's scanner picks them up (same convention as
// CandidateInsightsChips.vue).
const SELECTED_PILL: Record<FilterChipCategory, string> = {
  preference: 'border-highlight-100 bg-highlight-0 text-highlight-700',
  intent: 'border-inform-100 bg-inform-0 text-inform-700',
  language: 'border-inform-100 bg-inform-0 text-inform-700',
};

const UNSELECTED_PILL = 'border-tint-80 bg-tint-0 text-shade-880 hover:bg-tint-40';

// The count sits a shade lighter than the label so it never competes with it — one step
// down within the same family, matching the card chips' note treatment.
const SELECTED_COUNT: Record<FilterChipCategory, string> = {
  preference: 'text-highlight-600',
  intent: 'text-inform-600',
  language: 'text-inform-600',
};

const pillClass = computed(() =>
  props.selected ? SELECTED_PILL[props.category] : UNSELECTED_PILL,
);

const countClass = computed(() =>
  props.selected ? SELECTED_COUNT[props.category] : 'text-shade-800',
);

// The template must keep exactly one root node: anything beside it, a stray comment
// included, makes the component a fragment and silently drops inherited classes and
// listeners. Comments about the markup therefore live here rather than in the template.
//
// The chip sizes with min-h + py rather than a fixed h-6, so a single-line chip measures the
// design's 24px while a label that wraps in the 232px sidebar grows the chip rather than
// spilling past its border. The 18px line box is deliberately tighter than the 14px label's
// natural leading: at 21px the padded content would exceed 24px and the chip would outgrow
// the row it sits in.
</script>

<template>
  <button
    type="button"
    :aria-pressed="selected"
    class="flex min-h-6 items-center gap-1 rounded-[14px] border px-2 py-0.5 text-sm font-normal leading-[18px] transition-colors"
    :class="pillClass"
    @click="$emit('toggle')"
  >
    <span>{{ label }}</span>
    <span v-if="count !== undefined" :class="countClass">
      <!-- Decorative separator: a screen reader should read "1st shift, 87", not the bullet. -->
      <span aria-hidden="true">•</span>
      {{ count }}
    </span>
  </button>
</template>
